import React, { useEffect, useMemo, useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-select";
import api from "../../services/api";
import config from "../../config";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import Paginator from "../../shared/Paginator";
import { Toast } from "../../shared/Toast";
import closeSvg from "../../assets/images/uncheck.svg";
import { stateOptions as allStates, alphabets, BrandsTypes, campaignStatus, slotStatusOptions } from "../../utils/constants";
import { useSelector } from "react-redux";
import { formatDateWithTimeYear } from "../../utils/dateFormat";
import "../creatives/CreativeList.scss";

const formatDate = (date) => new Date(date).toISOString().split("T")[0];
const statusLabels = {
  1: "Created",
  2: "Running",
  3: "Paused",
  4: "Completed",
  5: "Scheduled",
  6: "Approved",
};

function LiveCampaigns() {
  const salesUsersList = useSelector((state) => state.Head.subordinates);
  const [showUsersFilter, setShowUsersFilter] = useState(false)
  const allowedNewUserTypes = [...new Set(salesUsersList.map(item => item.new_user_type))]
  const [tempSalesUsersList, setTempSalesUsersList] = useState(salesUsersList)
  const [selectedList, setSelectedList] = useState(localStorage.getItem("selectedSalesList") && JSON.parse(localStorage.getItem("selectedSalesList")).length ? salesUsersList.filter(item => JSON.parse(localStorage.getItem("selectedSalesList"))?.includes(item.value)) : salesUsersList?.length > 1 ? salesUsersList : []);
  const [filterActiveLetters, setFilterActiveLetters] = useState([...new Set(
    salesUsersList.map((item) => {
      return item?.label[0]?.toUpperCase()
    }
    ).sort()
  )]);
  const [newUserTypeInfilter, setNewUserTypeInfilter] = useState(0)
  const [searchFilter, setSearchFilter] = useState('')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const today = formatDate(new Date());
  const [dateRange, setDateRange] = useState({
    startDate: today,
    endDate: today,
  });
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loader, setLoader] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedCampaignStatus, setSelectedCampaignStatus] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchDistrictOptions();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedList, selectedDistrict, dateRange, selectedCampaignStatus]);

  useEffect(() => {
    fetchLiveCampaigns();
  }, [currentPage, selectedList, selectedDistrict, dateRange, selectedCampaignStatus]);



  const fetchDistrictOptions = async () => {
    try {
      const states = Array.from(new Set(allStates.map((item) => item.state_id)));
      const langs = Array.from(new Set(allStates.map((item) => item.lang_id)));
      const res = await api({
        url: "/location/getDistrictsByStateId",
        method: "POST",
        responseType: "json",
        data: { states, langs },
      });
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const options = list
        .map((item) => ({
          value: item?.dist_id ?? item?.value ?? item?.id,
          label: item?.dist_name ?? item?.label ?? item?.name,
        }))
        .filter((option) => option.value !== undefined && option.label);
      setDistrictOptions(options);
    } catch (error) {
      console.error("Error loading districts", error);
      Toast("error", "Unable to load districts");
      setDistrictOptions([]);
    }
  };

  const buildPayload = useMemo(
    () => ({
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
      sales_person_id: selectedList.map(item => item.value),
      district_id: selectedDistrict?.value ? [selectedDistrict?.value] : null,
      status: selectedCampaignStatus?.value ? selectedCampaignStatus?.value : null,
      page: currentPage,
      limit: config.PER_PAGE,
    }),
    [currentPage, dateRange, selectedDistrict, selectedList, selectedCampaignStatus]
  );

  const fetchLiveCampaigns = async () => {
    try {
      setLoader(true);
      const res = await api({
        url: "/live/get",
        method: "POST",
        responseType: "json",
        data: buildPayload,
      });
      if (res?.code !== 200) {
        setCampaigns([]);
        setPageCount(0);
        if (res?.message) Toast("error", res.message);
        setLoader(false);
        return;
      }
      const resultData = res?.data?.data ?? res?.data ?? [];
      const total = res?.data?.total ?? resultData?.length ?? 0;
      setCampaigns(Array.isArray(resultData) ? resultData : []);
      setPageCount(Math.ceil(total / config.PER_PAGE));
      setLoader(false);
    } catch (error) {
      console.error("Error fetching live campaigns", error);
      Toast("error", "Unable to fetch live campaigns");
      setCampaigns([]);
      setPageCount(0);
      setLoader(false);
    }
  };

  const handleValueChange = (value) => {
    setDateRange(value ?? { startDate: today, endDate: today });
  };

  const resetFilters = () => {
    setDateRange({ startDate: today, endDate: today });
    setSelectedList([]);
    setSelectedDistrict(null);
    setNewUserTypeInfilter(0);
    setSearchFilter('');
    setTempSalesUsersList(salesUsersList);
    setFilterActiveLetters([...new Set(
      salesUsersList.map((item) => {
        return item?.label[0]?.toUpperCase()
      }
      )).sort()]);
    setShowUsersFilter(false);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };
  const handleNewUsrTypChange = (new_user_type) => {
    try {
      setNewUserTypeInfilter(new_user_type)
      let filteredArray = salesUsersList
      if (searchFilter) {
        filteredArray = filteredArray.filter(item => item.label.toLowerCase().includes(searchFilter))
      }
      if (+new_user_type) {
        filteredArray = filteredArray.filter(item => +item.new_user_type === +new_user_type)
      }
      setTempSalesUsersList(filteredArray)
      setFilterActiveLetters([...new Set(
        filteredArray.map((item) => {
          return item?.label[0]?.toUpperCase()
        }
        ).sort()
      )])
    } catch (error) {
      console.error("Error @ handleNewUsrTypChange", error)
    }
  }

  const closeUsersFilter = () => {
    try {
      setShowUsersFilter(false)
    } catch (error) {
      console.error("Error @ closeUsersFilter", error)
    }
  }

  const scrollToSection = (id) => {
    document?.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  const formatStatus = (status) => statusLabels[status] ?? status ?? "-";
  // colored status chip (matches the shared card theme)
  const statusChipClass = (status) => {
    switch (+status) {
      case 2: return "cl-chip--active";   // Running
      case 6: return "cl-chip--active";   // Approved
      case 4: return "cl-chip--ready";    // Completed
      case 5: return "cl-chip--pending";  // Scheduled
      case 3: return "cl-chip--failed";   // Paused
      default: return "cl-chip--neutral"; // Created / unknown
    }
  };

  return (
    <div className="p-3 bg-white rounded shadow-sm flex flex-col h-[calc(100vh-72px)]">
      <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between mb-3 shrink-0" style={{ width: isMobile ? '100%' : 'auto' }}>
        <div className="d-flex flex-wrap gap-3 align-items-end" style={{ width: isMobile ? '100%' : 'auto' }}>
          <div style={{ width: isMobile ? '100%' : 'auto' }}>
            <label className="form-label mb-1 mobHide">Date Range</label>
            <div className="border rounded-lg  border-gray-300 hover:cursor-pointer hover:border-2 hover:border-sky-500" style={{ width: isMobile ? '100%' : 'auto' }}>
              <Datepicker
                classNames="form-input"
                value={dateRange}
                primaryColor="cyan"
                showShortcuts
                onChange={handleValueChange}
              />
            </div>
          </div>
          {salesUsersList?.length > 1 ?  <div style={{ width: isMobile ? '100%' : 'auto' }}>
          <label className="form-label mb-1 mobHide">Sales Person</label>
          <div className="select-sales-users" onClick={() => { setShowUsersFilter(true) }} style={{ width: isMobile ? '100%' : 'auto' }}>
            Select Users
          </div> 
          </div>: null}
          <div style={{ width: isMobile ? '100%' : 'auto', minWidth: isMobile ? '100%' : '200px' }}>
            <label className="form-label mb-1 mobHide">District</label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={districtOptions}
              value={selectedDistrict}
              isClearable
              isSearchable
              placeholder="All districts"
              onChange={setSelectedDistrict}
            />
          </div>
          <div style={{ width: isMobile ? '100%' : 'auto', minWidth: isMobile ? '100%' : '150px' }}>
            <label className="form-label mb-1 mobHide">Status</label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={campaignStatus.filter(item => item.value !== 6 && item.value !== 1)}
              value={selectedCampaignStatus}
              isClearable
              isSearchable
              placeholder="All status"
              onChange={setSelectedCampaignStatus}
            />
          </div>
        </div>
        <div className="d-flex gap-2" style={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-start' : 'flex-end', marginTop: isMobile ? '10px' : '0' }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={resetFilters} style={{ width: isMobile ? '100%' : 'auto' }}>
            Reset
          </button>
        </div>
      </div>

      <section className="mt-3 flex min-h-0 flex-1 flex-col">
        <fieldset className="border rounded p-3 flex min-h-0 flex-1 flex-col" style={{ width: '100%', overflow: 'hidden' }}>
          <legend className="float-none w-auto px-2 fs-6 fw-semibold mb-0">
            Live Campaigns
          </legend>
          <div className="creativeListPage flex min-h-0 flex-1 flex-col">
            <div className="cl-table-wrap flex-1 min-h-0 overflow-y-auto">
              <table className="cl-table cl-table--multi">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Campaign Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Target</th>
                    <th>Status</th>
                    <th>Deeplink</th>
                  </tr>
                </thead>
                {loader ? (
                  <TableLoader colspan={7} />
                ) : campaigns?.length ? (
                  <tbody>
                    {campaigns.map((camp, index) => (
                      <tr key={camp?.campaign_id ?? index}>
                        <td data-label="S.No">{currentPage * config.PER_PAGE + index + 1}</td>
                        <td className="cl-name" data-label="Campaign Name">{camp?.campaign_name ?? "-"}</td>
                        <td data-label="Start Date">{camp?.start_date ? formatDateWithTimeYear(camp?.start_date) : "-"}</td>
                        <td data-label="End Date">{camp?.end_date ? formatDateWithTimeYear(camp?.end_date) : "-"}</td>
                        <td className="cl-cell-wrap" data-label="Target">
                          <div className="cl-clamp" title={camp?.target}>{camp?.target || "-"}</div>
                        </td>
                        <td className="cl-inline" data-label="Status">
                          <span className={`cl-chip ${statusChipClass(camp?.status)}`}>{formatStatus(camp?.status)}</span>
                        </td>
                        <td className="cl-inline cl-inline--end" data-label="Deeplink">
                          {camp?.deeplink ? (
                            <div className="cl-actions">
                              <sl-tooltip content="Open link">
                                <sl-icon-button
                                  library="lucide"
                                  name="external-link"
                                  onClick={() => window.open(camp.deeplink, "_blank", "noopener,noreferrer")}
                                ></sl-icon-button>
                              </sl-tooltip>
                              <sl-tooltip content="Copy link">
                                <sl-icon-button
                                  library="lucide"
                                  name="copy"
                                  onClick={() => {
                                    try {
                                      navigator.clipboard.writeText(camp.deeplink);
                                      Toast("success", "Link copied");
                                    } catch (e) {
                                      Toast("error", "Could not copy link");
                                    }
                                  }}
                                ></sl-icon-button>
                              </sl-tooltip>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <NoDataFound colspan={7} />
                )}
              </table>
            </div>
          </div>
          {pageCount > 1 && (
            <div className="d-flex mt-2 shrink-0" style={{
              justifyContent: isMobile ? 'center' : 'flex-end',
              width: '100%',
              overflowX: 'auto',
              padding: isMobile ? '10px 0' : '0'
            }}>
              <Paginator pageCount={pageCount} handlePageClick={handlePageClick} forcePage={currentPage} />
            </div>
          )}
        </fieldset>
      </section>
      {showUsersFilter ? <div className="usersMultiSelect" onClick={() => closeUsersFilter()}>
        <div class="popupuserlist" onClick={(e) => e.stopPropagation()}>
          <div class="userlist-header">
            <div class="alphabiticals">
              <ul>
                {alphabets.map(alphabet => {
                  return <li
                    onClick={() => scrollToSection(alphabet)}
                    className={`${filterActiveLetters.includes(alphabet) ? "" : "inactive"
                      }`}

                  >
                    {alphabet}
                  </li>
                })}

              </ul>
            </div>
            <ul class="new-user-type">
              <li key={0} onClick={() => { handleNewUsrTypChange(0) }}>
                <label className={+newUserTypeInfilter === 0 ? 'active' : ''} onClick={() => handleNewUsrTypChange(0)}>
                  All
                </label>
              </li>
              {BrandsTypes.filter(item => allowedNewUserTypes.includes(item.value)).map((item) => (
                <li key={item.value} onClick={() => { handleNewUsrTypChange(item.value) }}>
                  <label className={+newUserTypeInfilter === item.value ? 'active' : ''} onClick={() => handleNewUsrTypChange(item.value)}>
                    {item.label}
                  </label>
                </li>
              ))}
            </ul>
            <div class="searchbox">
              <input class="form-control" placeholder="search" value={searchFilter} onChange={(e) => {
                setSearchFilter(e.target.value)
                let tempString = e.target.value.toLowerCase()
                let filteredArray = salesUsersList
                if (newUserTypeInfilter) {
                  filteredArray = filteredArray.filter(item => item.new_user_type === newUserTypeInfilter)
                }
                if (tempString) {
                  filteredArray = filteredArray.filter(item => item.label.toLowerCase().includes(tempString))
                  setTempSalesUsersList(filteredArray)
                  setFilterActiveLetters([...new Set(
                    filteredArray.map((item) => {
                      return item?.label[0]?.toUpperCase()
                    }
                    ).sort()
                  )])
                }
                else {
                  setTempSalesUsersList(filteredArray)
                  setFilterActiveLetters([...new Set(
                    salesUsersList.map((item) => {
                      return item?.label[0]?.toUpperCase()
                    }
                    ).sort()
                  )])
                }
              }}
              />
            </div>
          </div>

          <div class={`userlistbody ${isMobile ? '!h-[50vh]' : ''}`}>
            {filterActiveLetters.map(alphabet => {
              let filteredUsers = tempSalesUsersList.filter(item => item.label[0].toUpperCase() === alphabet)
              return (<div class="alphabiticalletterelement">
                <div class="alphabiticalletter" id={alphabet}>
                  <span>{alphabet}</span>
                </div>

                <div class="userlist">
                  <ul>
                    {filteredUsers.map(user =>
                      <li onClick={() => { if (!selectedList.find(item => item.value === user.value)) { setSelectedList([...selectedList, user]); localStorage.setItem("selectedSalesList", JSON.stringify([...selectedList.map(item => item.value), user.value])) } else { setSelectedList([...selectedList.filter(item => item.value !== user.value)]); localStorage.setItem("selectedSalesList", JSON.stringify([...selectedList.filter(item => item.value !== user.value)].map(item => item.value))) } }} className={selectedList.find(item => item.value === user.value) ? "active" : ""}>
                        {user.label}
                      </li>)}
                  </ul>
                </div>
              </div>)

            }
            )}
          </div>

          <div class="userlistfooter">
            <div class="btnuserdetails">
              <button class="selectall" type="button" onClick={() => { setSelectedList([]); localStorage.setItem('selectedSalesList', JSON.stringify([])) }}>Clear All</button>
              <button class="selectonebyone" type="button" onClick={() => { setSelectedList([...new Set(salesUsersList.filter(item => [...selectedList.map(item => item.value), ...tempSalesUsersList.map(item => item.value)].includes(item.value)))]); localStorage.setItem("selectedSalesList", JSON.stringify([...new Set(salesUsersList.filter(item => [...selectedList.map(item => item.value), ...tempSalesUsersList.map(item => item.value)].includes(item.value)))].map(item => item.value))) }}>Select All</button>
              <button type="button" onClick={() => closeUsersFilter()}>Apply</button>
            </div>
          </div>
          <button
            className="close-btn"
            onClick={() => closeUsersFilter()}
          >
            <img src={closeSvg} alt="close2" />
          </button>
        </div>
      </div> : null}
    </div>
  );
}

export default LiveCampaigns;
